import { useCallback, useMemo, useState } from 'react';
import * as atlas from 'azure-maps-control';
import { Check, Edit2, X } from 'react-feather';
import styled from '@emotion/styled';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import cuid2 from '@paralleldrive/cuid2';
import type { LocationInfo, LocationItem, SmartRoutingEntityType, SmartRoutingV2 } from '@trinity/chat';
import { LocationProviderType } from '@trinity/chat';
import { AzureMap } from '@trinity/chat-components/components/AzureMap';
import { SelectLocation } from '@trinity/chat-components/components/Search/Location';
import { useBrowserLocation } from '@trinity/chat-components/hooks/useBrowserLocation';
import { type BuildMapLinkParams, type LocationDescriptor } from '../../lib';
import { OpenMapsButton } from '../Chat/OpenMapsButton';
import { getSmartRoutingEntityCollectiveLabel } from './entity-copy';
import type { SmartRoutingAction } from './SmartRouting.Container';

type Props = {
  state: SmartRoutingV2;
  dispatch: (action: SmartRoutingAction) => void;
  validEntities: SmartRoutingEntityType[];
};

function computeRouteHeaderCopy(validEntities: SmartRoutingEntityType[]): string {
  const entityLabel = getSmartRoutingEntityCollectiveLabel(validEntities);
  return `Based on your selected ${entityLabel} we've generated an efficient route to maximize your visits while minimizing travel time.`;
}

function computeStartNameFromLocation(
  startingLocation: NonNullable<SmartRoutingV2['startingLocation']>
): string | undefined {
  if (startingLocation.type === 'entity') {
    return startingLocation.entityName;
  }
  if (startingLocation.type === 'search') {
    return startingLocation.name;
  }
  if (startingLocation.type === 'browser') {
    return `Current Location (${startingLocation.latitude}, ${startingLocation.longitude})`;
  }
  return undefined;
}

function computeStartName(state: SmartRoutingV2): string | undefined {
  const defaultName = 'Start Location';
  if (!state.routingResult) return defaultName;

  if (state.startingLocation) {
    return computeStartNameFromLocation(state.startingLocation);
  }

  const startPoint = state.routingResult.legs[0].start;
  if (startPoint.entity) {
    return startPoint.entity.name;
  }

  return startPoint.address || defaultName;
}

export const SmartRoutingResult = ({ state, dispatch, validEntities }: Props) => {
  const mapParams = useMemo(() => {
    if (!state.routingResult) return null;

    const points = [
      state.routingResult.legs[0].start,
      ...state.routingResult.legs.map((leg) => leg.end),
    ].map<LocationDescriptor>((point) => {
      if (point.address) {
        return {
          type: 'address',
          address: point.address,
        };
      } else {
        return {
          type: 'coords',
          latitude: point.latitude,
          longitude: point.longitude,
        };
      }
    });

    const mapsParams: BuildMapLinkParams = {
      origin: points[0],
      destination: points[points.length - 1],
      waypoints: points.slice(1, -1),
    };

    return mapsParams;
  }, [state.routingResult]);

  if (!state.routingResult) {
    return null;
  }

  return (
    <div>
      <EditableRouteLegs state={state} dispatch={dispatch} validEntities={validEntities} />
      <RouteResultMap state={state} />
      {mapParams && (
        <OpenMapsButton mapParams={mapParams}>
          <AutoAwesomeIcon style={{ fontSize: 18 }} /> Get Directions
        </OpenMapsButton>
      )}
    </div>
  );
};

type RouteLegProps = {
  ordinal: number;
  name: string;
  location: string;
  distance: string;
};

const RouteLeg = (props: RouteLegProps) => {
  return (
    <Root>
      <Em>{`${props.ordinal}. ${props.name}`}</Em>
      <div>{`-`}</div>
      <div>{props.location}</div>
      <div>{`-`}</div>
      <div>{props.distance}</div>
    </Root>
  );
};

type EditableRouteLegProps = {
  latitude: number;
  longitude: number;
  entityId: string | number | null;
  setForm: (data: Omit<LocationItem, 'name'> & { location: string }) => void;
} & RouteLegProps;

const EditableRouteLeg = ({ ordinal, setForm, ...props }: EditableRouteLegProps) => {
  const handleChange = useCallback(
    (data: LocationItem) => {
      setForm({
        latitude: data.latitude,
        longitude: data.longitude,
        location: data.name,
        id: data.id ?? ordinal.toString(),
      });
    },
    [ordinal, setForm]
  );

  return (
    <Root>
      <Em>{`${ordinal}. ${props.name}`}</Em>
      <div>
        <SelectLocation
          onChange={handleChange}
          placeholder={props.location}
          value={{
            latitude: props.latitude,
            longitude: props.longitude,
            id: ordinal.toString(),
            name: props.location,
          }}
        />
      </div>
    </Root>
  );
};

type FormItem = Omit<LocationItem, 'id' | 'name'> & { id?: string; location: string };

const EditableRouteLegs = ({ state, dispatch, validEntities }: Props) => {
  const { getLocation } = useBrowserLocation({ onError: () => {}, onSuccess: () => {} });
  const [editing, setEditing] = useState(false);

  const buildLeg = useCallback(
    (leg: NonNullable<SmartRoutingV2['routingResult']>['legs'][0], ordinal: number) => {
      const entity = leg.end.entity;

      return {
        ordinal: ordinal + 1,
        name: entity.name,
        location: leg.end.address || `(${leg.end.longitude}, ${leg.end.latitude})`,
        distance: `${leg.distanceMiles.toFixed(1)} miles`,
        latitude: leg.end.latitude,
        longitude: leg.end.longitude,
        entityId: entity.id,
      };
    },
    []
  );

  const legs = useMemo(() => {
    if (!state.routingResult) return [];
    return state.routingResult.legs.map((x, i) => buildLeg(x, i));
  }, [buildLeg, state.routingResult]);

  const getInitialState = useCallback(() => {
    return legs.reduce(
      (acc, x) => ({
        ...acc,
        [x.ordinal]: {
          latitude: x.latitude,
          longitude: x.longitude,
          location: x.location,
          id: null,
        },
      }),
      {} as Record<number, FormItem>
    );
  }, [legs]);

  const [startLocation, setStartLocation] = useState<FormItem>(() => {
    if (!state.routingResult) return { latitude: 0, longitude: 0, location: '' };

    const startPoint = state.routingResult.legs[0].start;
    const location =
      startPoint.entity?.name || startPoint.address || `(${startPoint.longitude}, ${startPoint.latitude})`;

    return {
      latitude: startPoint.latitude,
      longitude: startPoint.longitude,
      location,
    };
  });

  const [form, setForm] = useState(getInitialState);

  const startName = useMemo(() => computeStartName(state), [state]);
  const routeHeaderCopy = useMemo(() => computeRouteHeaderCopy(validEntities), [validEntities]);

  const resetForm = useCallback(() => {
    setEditing(false);
    setForm(getInitialState());
  }, [getInitialState, setEditing]);

  const canSubmit = useMemo(() => {
    return Object.values(form).some((x) => x.id) || !!startLocation.id;
  }, [form, startLocation.id]);

  const handleSubmit = useCallback(() => {
    if (startLocation) {
      dispatch({
        type: 'submit-location',
        payload: {
          startingLocation: {
            type: 'search' as const,
            id: startLocation.id || 'updated-location',
            name: startLocation.location,
            latitude: startLocation.latitude,
            longitude: startLocation.longitude,
          },
        },
      });
      resetForm();
    } else {
      getLocation()
        .then((location) => {
          dispatch({
            type: 'submit-location',
            payload: {
              startingLocation: {
                type: 'browser' as const,
                latitude: location.latitude,
                longitude: location.longitude,
              },
            },
          });
          resetForm();
        })
        .catch(() => undefined);
    }
  }, [dispatch, startLocation, resetForm, getLocation]);

  if (!state.routingResult) {
    return null;
  }

  return (
    <Routes>
      <Header>{routeHeaderCopy}</Header>
      <Actions>
        {editing && (
          <CancelAction onClick={resetForm}>
            <CancelIcon size={18} />
          </CancelAction>
        )}
        {editing && canSubmit && (
          <SubmitAction onClick={handleSubmit}>
            <CheckIcon size={18} />
          </SubmitAction>
        )}
      </Actions>
      <RouteLegs>
        <EditableStartLocation
          name={startName}
          editing={editing}
          startLocation={startLocation}
          setStartLocation={setStartLocation}
        />
        {legs.map((x, i) => (
          <div key={i}>
            {!editing ? (
              <RouteLeg {...x} />
            ) : (
              <EditableRouteLeg
                {...x}
                {...form[x.ordinal]}
                setForm={(data) => setForm((form) => ({ ...form, [x.ordinal]: data }))}
              />
            )}
          </div>
        ))}
      </RouteLegs>
      <Total>
        <Em>{`Total Distance:`}</Em>
        <div>{`${state.routingResult.legs.reduce((acc, x) => acc + x.distanceMiles, 0).toFixed(1)} miles`}</div>
      </Total>
      <Header>{copy.footer}</Header>
    </Routes>
  );
};

type EditableStartLocationProps = {
  editing: boolean;
  startLocation: FormItem;
  setStartLocation: (data: FormItem) => void;
  name: string;
};

const EditableStartLocation = (props: EditableStartLocationProps) => {
  const currentName =
    props.startLocation.location ?? `(${props.startLocation.longitude}, ${props.startLocation.latitude})`;
  if (props.editing) {
    return (
      <Root>
        <Em>{props.name}</Em>
        <div>
          <SelectLocation
            onChange={(val) => props.setStartLocation({ ...val, location: val.name, id: 'changed-val' })}
            placeholder={currentName}
            value={{
              latitude: props.startLocation.latitude,
              longitude: props.startLocation.longitude,
              id: props.startLocation.id,
              name: props.startLocation.location,
            }}
          />
        </div>
      </Root>
    );
  } else {
    return (
      <Root>
        <Em>{props.name}</Em>
        {props.name ? '' : currentName}
      </Root>
    );
  }
};

const Routes = styled.div({
  position: 'relative',
});

const Actions = styled.div({
  display: 'flex',
  gap: 5,
  position: 'absolute',
  top: 30,
  right: 0,
});

const _EditIcon = styled(Edit2)(
  ({ theme }) => `
  color: ${theme.palette.primary.main};
  cursor: pointer;
`
);

const _EditAction = styled.div(
  ({ theme }) => `
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${theme.palette.primary.main};
  border-radius: 50%;
  width: 32px;
  height: 32px;
`
);

const CancelIcon = styled(X)(
  ({ theme }) => `
  color: ${theme.palette.black.main};
  cursor: pointer;
`
);

const CancelAction = styled.div(
  ({ theme }) => `
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${theme.palette.black.main};
  border-radius: 50%;
  width: 32px;
  height: 32px;
`
);

const CheckIcon = styled(Check)(
  ({ theme }) => `
  color: ${theme.palette.green.main};
  cursor: pointer;
`
);

const SubmitAction = styled.div(
  ({ theme }) => `
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${theme.palette.green.main};
  border-radius: 50%;
  width: 32px;
  height: 32px;
`
);

const RouteLegs = styled.div({
  margin: '15px 0',
});

const Root = styled.div({
  display: 'flex',
  gap: 5,
  alignItems: 'center',
  marginBottom: 10,
});

const Total = styled.div({
  display: 'flex',
  gap: 5,
  marginBottom: 10,
});

const Em = styled.div({
  fontFamily: 'var(--font-semibold)',
});

const copy = {
  footer: `This route is optimized to ensure efficient visits while minimizing travel time. You can now proceed with your day seamlessly!`,
};

const Header = styled.div({
  marginBottom: 10,
});

type MapsProps = Pick<Props, 'state'>;

const RouteResultMap = (props: MapsProps) => {
  if (!props.state.routingResult) return null;

  if (props.state.routingResult.provider === LocationProviderType.Azure) {
    return (
      <MapContainer>
        <AzureRouteMap {...props} />
      </MapContainer>
    );
  }
};

const AzureRouteMap = (props: MapsProps) => {
  const dataSourceId = useMemo(() => `route-${cuid2.createId()}`, []);

  const getPointName = useCallback((point: LocationInfo) => {
    if (point.address) {
      return point.address;
    }

    if (point.entity) {
      return point.entity.name;
    }

    return null;
  }, []);

  const features = useMemo(() => {
    if (!props.state.routingResult) return [];

    const routingResult = props.state.routingResult;

    return [
      new atlas.data.MultiLineString(
        routingResult.legs.map((l) => l.meta.points.map((p) => [p.longitude, p.latitude]))
      ),
      new atlas.data.Feature(
        new atlas.data.Point([routingResult.legs[0].start.longitude, routingResult.legs[0].start.latitude]),
        {
          title: getPointName(routingResult.legs[0].start) ?? 'Start',
          icon: 'pin-round-red',
        }
      ),
      ...routingResult.legs.map(
        (leg) =>
          new atlas.data.Feature(new atlas.data.Point([leg.end.longitude, leg.end.latitude]), {
            title: getPointName(leg.end),
            icon: 'pin-round-blue',
          })
      ),
    ];
  }, [getPointName, props.state.routingResult]);

  const layers = useMemo(() => {
    return [
      new atlas.layer.LineLayer(dataSourceId, 'lines', {
        strokeColor: '#2272B9',
        strokeWidth: 5,
        lineJoin: 'round',
        lineCap: 'round',
      }),
      new atlas.layer.SymbolLayer(dataSourceId, 'icon-symbols', {
        iconOptions: {
          image: ['get', 'icon'],
          allowOverlap: true,
          ignorePlacement: true,
          offset: [0, 0],
        },
        textOptions: {
          textField: ['get', 'title'],
          offset: [0, 1.2],
          haloColor: 'white',
          haloWidth: 1.5,
        },
        filter: ['any', ['==', ['geometry-type'], 'Point'], ['==', ['geometry-type'], 'MultiPoint']], //Only render Point or MultiPoints in this layer.
      }),
    ];
  }, [dataSourceId]);

  if (!props.state.routingResult) return null;

  return (
    <AzureMap features={features} layers={layers} dataSourceId={dataSourceId} height="100%" width="100%" />
  );
};

const MapContainer = styled.div({
  height: 300,
  width: '100%',
  marginTop: 15,
});
